import axios from "axios";
import { resetAllSlices } from "store";
import { router } from "index";

export const http = axios.create({ baseURL: process.env.REACT_APP_API_BASE_URL });

type TokenRetriever = () => Promise<string | undefined>;

let requestInterceptorId: number | undefined;
let responseInterceptorId: number | undefined;

export const attachAxiosAuthInterceptors = (
  getAccessToken: TokenRetriever
) => {
  detachAxiosAuthInterceptors();
  
  requestInterceptorId = http.interceptors.request.use(
    async (config) => {
      let token: string | undefined;
      try {
        token = await getAccessToken();
      } catch {
        // Only reset and redirect if we're not already on the login page
        // to avoid a reset loop that prevents tenant loading
        if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
          resetAllSlices();
          router.navigate("/login");
        }
        return Promise.reject(new axios.Cancel("Session expired"));
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        delete config.headers.Authorization;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = http.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
          resetAllSlices();
          router.navigate("/login");
        }
        return Promise.reject(error?.response?.data ?? error);
      }

      if (error?.response?.status === 403) {
        router.navigate("/forbidden");
        return Promise.reject(error?.response?.data ?? error);
      }

      return Promise.reject(error?.response?.data ?? error);
    }
  );

  return detachAxiosAuthInterceptors;
};

export const detachAxiosAuthInterceptors = () => {
  if (typeof requestInterceptorId === "number") {
    http.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = undefined;
  }

  if (typeof responseInterceptorId === "number") {
    http.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = undefined;
  }
};
